import { documentMetadataStore } from '../stores/documentMetadataStore';
import { API_CONFIG } from '../config/api';

/**
 * Service for synchronizing document title from Patdraft to Patdraw
 * The title from the spec editor's title block is pushed to Patdraw
 */
export class DocumentTitleSyncService {
    constructor() {
        this._isSubscribed = false;
        this._lastSentTitle = null;
    }

    /**
     * Start monitoring document title changes and sync to Patdraw
     */
    startMonitoring() {
        if (this._isSubscribed) return;
        
        this._unsubscribe = documentMetadataStore.subscribe(metadata => {
            if (metadata.title && metadata.title !== this._lastSentTitle) {
                this.syncTitleToPatdraw(metadata.title);
            }
        });
        
        this._isSubscribed = true;
        console.log('Document title sync monitoring started');
    }

    /**
     * Stop monitoring title changes
     */
    stopMonitoring() {
        if (this._unsubscribe) {
            this._unsubscribe();
            this._isSubscribed = false;
            console.log('Document title sync monitoring stopped');
        }
    }

    /**
     * Send title update to Patdraw
     * @param {string} title - The document title to sync
     */
    async syncTitleToPatdraw(title) {
        try {
            const response = await fetch(
                `${API_CONFIG.PATDRAW_API_URL}/api/presentation/document-title`,
                {
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({ title })
                }
            );

            if (!response.ok) {
                throw new Error(`Failed to sync title: ${response.statusText}`);
            }

            const result = await response.json();
            if (result.success) {
                this._lastSentTitle = title;
                console.log(`Successfully synced title to Patdraw: "${title}"`);
            }
        } catch (error) {
            console.error('Failed to sync document title to Patdraw:', error);
            // Don't throw - title sync is not critical for app operation
        }
    }

    /**
     * Manually trigger a title sync
     * Useful for initial sync on app startup
     */
    async syncCurrentTitle() {
        return new Promise((resolve) => {
            const unsubscribe = documentMetadataStore.subscribe(metadata => {
                if (metadata.title) {
                    this.syncTitleToPatdraw(metadata.title).then(() => {
                        unsubscribe();
                        resolve();
                    });
                }
            });
        });
    }
}

// Create and export singleton instance
export const documentTitleSyncService = new DocumentTitleSyncService();